"use strict";

(function () {
  const IDS = {
    widget: "custom-chat-widget",
    closeBtn: "chat-close-btn",
    toggleBtn: "chat-toggle-btn",
    chatBox: "chat-box",
    minimizedIcon: "minimized-widget-icon",
    salesBtn: "chat-sales-btn",
    dynamicName: "dynamic-name",
  };

  const STORAGE_KEYS = {
    minimized: "auChatWidgetMinimized",
    mobileOpen: "auChatWidgetMobileOpen",
  };

  let resizeTimer = null;

  function getElement(id) {
    return document.getElementById(id);
  }

  function isMobileViewport() {
    return window.matchMedia("(max-width: 767px)").matches;
  }

  function setAriaExpanded(expanded) {
    const toggleBtn = getElement(IDS.toggleBtn);
    if (toggleBtn) {
      toggleBtn.setAttribute("aria-expanded", expanded ? "true" : "false");
    }
  }

  function trackWidgetEvent(action, href) {
    const widget = getElement(IDS.widget);
    const payload = {
      event: "chat_widget_click",
      chat_widget_action: action,
      chat_widget_agent: widget?.dataset.agentName || "Marco",
      chat_widget_region: widget?.dataset.region || "AU",
      chat_widget_href: href || "",
    };

    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push(payload);
    }

    if (typeof window.gtag === "function") {
      window.gtag("event", "chat_widget_click", {
        action,
        agent: payload.chat_widget_agent,
        region: payload.chat_widget_region,
        href: payload.chat_widget_href,
      });
    }
  }

  function updateGreeting() {
    const dynamicName = getElement(IDS.dynamicName);
    const contact = window?.leadData?.contact;
    if (!dynamicName) return;

    if (contact?.firstName) {
      dynamicName.innerText = `Hi ${contact.firstName}, I'm Marco 👋`;
      return;
    }

    dynamicName.innerText = "Hi, I'm Marco 👋";
  }

  function showMinimizedState() {
    const widget = getElement(IDS.widget);
    const minimizedIcon = getElement(IDS.minimizedIcon);
    const chatBox = getElement(IDS.chatBox);

    if (widget) widget.style.display = "none";
    if (minimizedIcon) minimizedIcon.style.display = "block";
    if (chatBox) chatBox.style.display = "none";
    setAriaExpanded(false);
  }

  function applyResponsiveState() {
    const widget = getElement(IDS.widget);
    const toggleBtn = getElement(IDS.toggleBtn);
    const chatBox = getElement(IDS.chatBox);
    const minimizedIcon = getElement(IDS.minimizedIcon);
    const isMinimized = sessionStorage.getItem(STORAGE_KEYS.minimized) === "1";
    const isMobile = isMobileViewport();

    if (!widget || !toggleBtn || !chatBox || !minimizedIcon) return;

    if (isMinimized) {
      showMinimizedState();
      return;
    }

    widget.style.display = "block";
    minimizedIcon.style.display = "none";

    if (isMobile) {
      toggleBtn.style.display = "flex";
      const shouldOpen = sessionStorage.getItem(STORAGE_KEYS.mobileOpen) === "1";
      chatBox.style.display = shouldOpen ? "block" : "none";
      setAriaExpanded(shouldOpen);
      return;
    }

    toggleBtn.style.display = "none";
    chatBox.style.display = "block";
    sessionStorage.removeItem(STORAGE_KEYS.mobileOpen);
    setAriaExpanded(true);
  }

  function toggleChatBox() {
    if (!isMobileViewport()) return;

    const chatBox = getElement(IDS.chatBox);
    if (!chatBox) return;

    const shouldOpen = chatBox.style.display !== "block";
    chatBox.style.display = shouldOpen ? "block" : "none";
    setAriaExpanded(shouldOpen);

    if (shouldOpen) {
      sessionStorage.setItem(STORAGE_KEYS.mobileOpen, "1");
      trackWidgetEvent("toggle_open");
      return;
    }

    sessionStorage.removeItem(STORAGE_KEYS.mobileOpen);
    trackWidgetEvent("toggle_close");
  }

  function minimizeChatWidget() {
    sessionStorage.setItem(STORAGE_KEYS.minimized, "1");
    sessionStorage.removeItem(STORAGE_KEYS.mobileOpen);
    showMinimizedState();
    trackWidgetEvent("minimize");
  }

  function restoreChatWidget() {
    sessionStorage.removeItem(STORAGE_KEYS.minimized);
    applyResponsiveState();
    trackWidgetEvent("restore");
  }

  function handleTrackedClick(event) {
    const target = event.currentTarget;
    const action = target?.dataset.chatAction;
    if (!action) return;
    trackWidgetEvent(action, target.href || "");
  }

  function handleSalesClick(event) {
    handleTrackedClick(event);
    minimizeChatWidget();
  }

  function bindEvents() {
    const closeBtn = getElement(IDS.closeBtn);
    const toggleBtn = getElement(IDS.toggleBtn);
    const minimizedIcon = getElement(IDS.minimizedIcon);
    const salesBtn = getElement(IDS.salesBtn);
    const trackedLinks = document.querySelectorAll("[data-chat-action]");

    closeBtn?.addEventListener("click", minimizeChatWidget);
    toggleBtn?.addEventListener("click", toggleChatBox);
    minimizedIcon?.addEventListener("click", restoreChatWidget);
    salesBtn?.addEventListener("click", handleSalesClick);

    trackedLinks.forEach((link) => {
      if (link.id === IDS.salesBtn) return;
      link.addEventListener("click", handleTrackedClick);
    });

    window.addEventListener("resize", () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(applyResponsiveState, 120);
    });
  }

  function init() {
    if (document.body?.dataset.chatWidgetInitialized === "true") return;
    document.body.dataset.chatWidgetInitialized = "true";

    updateGreeting();
    bindEvents();
    applyResponsiveState();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();

/* ─────────────────────────────────────────────────────────────────────────
 * Speediance chat widget — enhancement 2026-04-20.4
 *
 * Visual-first attention grab + best-effort audio chime.
 *
 * On first user interaction (scroll/click/touch):
 *   • Widget wiggles
 *   • Pulsing green halo runs around the WhatsApp button until dismissed
 *   • Red "1" notification badge on the WA button (like an unread message)
 *   • Speech-bubble tooltip slides out: "Got questions? I'm here →"
 *   • Audio chime plays (if the browser allows it — Chrome/Safari allow
 *     once a user gesture has occurred, which scroll/click satisfies)
 *
 * All effects stop the instant the user clicks any CTA (they've converted).
 *
 * Also:
 *   • Moves "Chat on WhatsApp" to the TOP of the CTA list
 *   • Renames "Book a Demo With Me" → "Book a Demo/Call With Me" (EN+DE+FR+NL)
 *
 * Debug API: window.chatWidgetEnhance.testBeep() / .testWiggle() / .reset()
 * ─────────────────────────────────────────────────────────────────────── */
(function enhanceChatWidget() {
  "use strict";

  var BEEP_KEY  = "chatWidgetBeeped-v4";
  var BADGE_KEY = "chatWidgetBadge-v4";
  var CONVERTED = "chatWidgetConverted-v4";
  var reducedMotion = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var LOG_PREFIX = "[chat-widget-enhance]";

  var demoRenames = [
    [/Book a Demo With Me/i,        "Book a Demo/Call With Me"],
    [/Demo mit mir buchen/i,        "Demo oder Anruf mit mir buchen"],
    [/Réserver une démo avec moi/i, "Réserver une démo ou un appel avec moi"],
    [/Boek een demo met mij/i,      "Boek een demo of gesprek met mij"],
  ];

  function log() { try { console.log.apply(console, [LOG_PREFIX].concat([].slice.call(arguments))); } catch (e) {} }

  function applyDemoRename(el) {
    if (!el) return;
    var t = el.textContent;
    for (var i = 0; i < demoRenames.length; i++) {
      if (demoRenames[i][0].test(t)) { el.textContent = t.replace(demoRenames[i][0], demoRenames[i][1]); return; }
    }
  }

  function normaliseAuSalesClass(widget) {
    if (!widget) return;
    var sales = widget.querySelector('.cta-sales');
    if (sales && !widget.querySelector('.cta-whatsapp')) {
      sales.classList.remove('cta-sales');
      sales.classList.add('cta-whatsapp');
      // Replace "Chat with Sales" → "Chat on WhatsApp" (AU store legacy label)
      if (/Chat with Sales/i.test(sales.textContent)) {
        sales.textContent = sales.textContent.replace(/Chat with Sales/i, 'Chat on WhatsApp');
      }
    }
  }

  function reorderCtas(cta) {
    if (!cta) return;
    var wa      = cta.querySelector(".cta-whatsapp");
    var demo    = cta.querySelector(".cta-demo");
    var support = cta.querySelector(".cta-support");
    if (wa      && cta.firstElementChild !== wa)                 cta.insertBefore(wa, cta.firstElementChild);
    if (demo    && wa && demo.previousElementSibling !== wa)     cta.insertBefore(demo, wa.nextElementSibling);
    if (support && cta.lastElementChild !== support)             cta.appendChild(support);
  }

  function injectStyles() {
    if (document.getElementById("chat-widget-enhance-css")) return;
    var style = document.createElement("style");
    style.id = "chat-widget-enhance-css";
    style.appendChild(document.createTextNode(
      "@keyframes chatEnhanceWiggle{" +
      " 0%,100%{transform:rotate(0)}15%{transform:rotate(-10deg)}30%{transform:rotate(9deg)}45%{transform:rotate(-6deg)}60%{transform:rotate(4deg)}78%{transform:rotate(-2deg)}}" +
      ".chat-wiggle{animation:chatEnhanceWiggle 1s cubic-bezier(.36,.07,.19,.97);transform-origin:center}" +
      "@keyframes chatEnhanceHalo{" +
      " 0%{box-shadow:0 0 0 0 rgba(37,211,102,.7)}" +
      " 70%{box-shadow:0 0 0 18px rgba(37,211,102,0)}" +
      " 100%{box-shadow:0 0 0 0 rgba(37,211,102,0)}" +
      "}" +
      ".cta-buttons a.cta-whatsapp.chat-halo{animation:chatEnhanceHalo 1.6s ease-out infinite;position:relative}" +
      ".cta-buttons a.cta-whatsapp{background:linear-gradient(135deg,#25D366,#075E54)!important;box-shadow:0 4px 14px rgba(37,211,102,.45);font-weight:600;position:relative;color:#fff}" +
      ".chat-badge{position:absolute;top:-6px;right:-6px;min-width:20px;height:20px;padding:0 5px;background:#ef4444;color:#fff;font-size:12px;font-weight:800;line-height:20px;border-radius:10px;text-align:center;box-shadow:0 2px 6px rgba(239,68,68,.55);pointer-events:none;animation:chatBadgePop .5s cubic-bezier(.34,1.56,.64,1)}" +
      "@keyframes chatBadgePop{0%{transform:scale(0)}100%{transform:scale(1)}}" +
      "#chat-enhance-bubble{position:absolute;bottom:calc(100% + 14px);right:0;max-width:240px;padding:10px 14px;background:#111827;color:#fff;font-size:13px;line-height:1.35;border-radius:14px 14px 2px 14px;box-shadow:0 8px 24px rgba(0,0,0,.28);opacity:0;transform:translateY(6px) scale(.96);transition:opacity .25s ease,transform .25s ease;pointer-events:auto;z-index:10}" +
      "#chat-enhance-bubble.show{opacity:1;transform:translateY(0) scale(1)}" +
      "#chat-enhance-bubble::after{content:\"\";position:absolute;bottom:-6px;right:18px;width:12px;height:12px;background:#111827;transform:rotate(45deg)}" +
      "#chat-enhance-bubble .close{position:absolute;top:2px;right:6px;background:none;border:0;color:#9ca3af;font-size:16px;line-height:1;cursor:pointer;padding:2px 4px}" +
      "#chat-enhance-bubble .close:hover{color:#fff}" +
      "@media (prefers-reduced-motion: reduce){.chat-wiggle,.cta-buttons a.cta-whatsapp.chat-halo,.chat-badge{animation:none!important}}"
    ));
    (document.head || document.documentElement).appendChild(style);
    log("styles injected");
  }

  // Shared AudioContext — must be created under user gesture
  var sharedCtx = null;
  function ensureAudioCtx() {
    if (sharedCtx) {
      if (sharedCtx.state === "suspended" && typeof sharedCtx.resume === "function") {
        return sharedCtx.resume().then(function () { return sharedCtx; });
      }
      return Promise.resolve(sharedCtx);
    }
    var Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return Promise.reject(new Error("no AudioContext"));
    sharedCtx = new Ctx();
    // iOS/Safari unlock: play a silent one-sample buffer within user gesture
    try {
      var s = sharedCtx.createBufferSource();
      s.buffer = sharedCtx.createBuffer(1, 1, sharedCtx.sampleRate);
      s.connect(sharedCtx.destination);
      s.start(0);
    } catch (e) {}
    if (sharedCtx.state === "suspended" && typeof sharedCtx.resume === "function") {
      return sharedCtx.resume().then(function () { return sharedCtx; });
    }
    return Promise.resolve(sharedCtx);
  }

  function playChime(opts) {
    opts = opts || {};
    if (!opts.force && (sessionStorage.getItem(BEEP_KEY) === "1" || reducedMotion)) return;
    ensureAudioCtx().then(function (ctx) {
      if (!ctx || ctx.state !== "running") { log("audio ctx not running:", ctx && ctx.state); return; }
      var now = ctx.currentTime;
      // Master gain with a compressor so the peaks land LOUD (0.8) without clipping
      var comp = ctx.createDynamicsCompressor();
      comp.threshold.value = -12;
      comp.knee.value = 8;
      comp.ratio.value = 3;
      comp.attack.value = 0.003;
      comp.release.value = 0.12;
      comp.connect(ctx.destination);
      function note(freq, start, dur, peak) {
        var osc = ctx.createOscillator();
        var gain = ctx.createGain();
        osc.type = "triangle";
        osc.frequency.value = freq;
        osc.connect(gain); gain.connect(comp);
        gain.gain.setValueAtTime(0.0001, now + start);
        gain.gain.exponentialRampToValueAtTime(peak, now + start + 0.012);
        gain.gain.exponentialRampToValueAtTime(0.0001, now + start + dur);
        osc.start(now + start);
        osc.stop(now + start + dur + 0.05);
      }
      // C6 → E6 → G6 — classic ascending triad, loud peaks
      note(1047, 0.00, 0.22, 0.85);
      note(1319, 0.12, 0.26, 0.80);
      note(1568, 0.26, 0.34, 0.75);
      sessionStorage.setItem(BEEP_KEY, "1");
      log("chime played");
    }).catch(function (err) { log("chime error", err && err.message); });
  }

  function isVisible(el) {
    if (!el) return false;
    var cs = window.getComputedStyle(el);
    if (cs.display === "none" || cs.visibility === "hidden") return false;
    return el.offsetWidth > 0 || el.offsetHeight > 0;
  }

  function wiggleWidget() {
    var widget = document.getElementById("custom-chat-widget");
    var toggle = document.getElementById("chat-toggle-btn");
    var mini   = document.getElementById("minimized-widget-icon");
    [widget, toggle, mini].forEach(function (el) {
      if (!isVisible(el)) return;
      el.classList.remove("chat-wiggle");
      void el.offsetWidth;
      el.classList.add("chat-wiggle");
      setTimeout(function () { el.classList.remove("chat-wiggle"); }, 1000);
    });
  }

  function ensureHalo() {
    var wa = document.querySelector("#custom-chat-widget .cta-whatsapp");
    if (wa) wa.classList.add("chat-halo");
  }
  function stopHalo() {
    var wa = document.querySelector("#custom-chat-widget .cta-whatsapp");
    if (wa) wa.classList.remove("chat-halo");
  }

  function ensureBadge() {
    if (sessionStorage.getItem(BADGE_KEY) === "dismissed") return;
    var wa = document.querySelector("#custom-chat-widget .cta-whatsapp");
    if (!wa || wa.querySelector(".chat-badge")) return;
    var badge = document.createElement("span");
    badge.className = "chat-badge";
    badge.setAttribute("aria-hidden", "true");
    badge.textContent = "1";
    wa.appendChild(badge);
  }

  function clearBadge() {
    sessionStorage.setItem(BADGE_KEY, "dismissed");
    var badge = document.querySelector("#custom-chat-widget .cta-whatsapp .chat-badge");
    if (badge) badge.remove();
  }

  var bubbleEl = null;
  function ensureBubble() {
    if (bubbleEl || sessionStorage.getItem("chatWidgetBubbleDismissed-v4") === "1") return;
    var widget = document.getElementById("custom-chat-widget");
    if (!widget) return;
    bubbleEl = document.createElement("div");
    bubbleEl.id = "chat-enhance-bubble";
    bubbleEl.innerHTML = '<button class="close" type="button" aria-label="Dismiss">&times;</button>' +
      '<strong>Got questions?</strong><br>Message us on WhatsApp — we usually reply within a few minutes.';
    widget.appendChild(bubbleEl);
    bubbleEl.querySelector(".close").addEventListener("click", function (e) {
      e.stopPropagation();
      dismissBubble();
    });
    // Show after a small delay so the entrance is noticed
    requestAnimationFrame(function () { setTimeout(function () { bubbleEl.classList.add("show"); }, 200); });
  }

  function dismissBubble() {
    sessionStorage.setItem("chatWidgetBubbleDismissed-v4", "1");
    if (!bubbleEl) return;
    bubbleEl.classList.remove("show");
    setTimeout(function () { if (bubbleEl && bubbleEl.parentNode) bubbleEl.parentNode.removeChild(bubbleEl); bubbleEl = null; }, 260);
  }

  function hasConverted() { return sessionStorage.getItem(CONVERTED) === "1"; }
  function markConverted() { sessionStorage.setItem(CONVERTED, "1"); }

  function fireAttentionBurst() {
    if (hasConverted()) return;
    wiggleWidget();
    ensureHalo();
    ensureBadge();
    ensureBubble();
    playChime();
  }

  function scheduleAttentionNudge() {
    if (hasConverted() || reducedMotion) { log("skip nudge"); return; }

    var userInteracted = false;
    var firstTimer = null;
    var repeatTimer = null;

    function onInteract() {
      if (userInteracted) return;
      userInteracted = true;
      log("first interaction seen");
      ensureAudioCtx(); // warm up audio context inside gesture
      firstTimer = setTimeout(function () {
        if (hasConverted()) return;
        fireAttentionBurst();
      }, 1000);

      // Persistent silent wiggles at 45s and 90s
      repeatTimer = setTimeout(function rep1() {
        if (hasConverted()) return;
        wiggleWidget();
        repeatTimer = setTimeout(function rep2() {
          if (hasConverted()) return;
          wiggleWidget();
        }, 45000);
      }, 45000);
    }

    ["click", "scroll", "touchstart", "keydown", "pointerdown"].forEach(function (ev) {
      window.addEventListener(ev, onInteract, { passive: true, once: true });
    });

    // On CTA click → stop everything
    document.querySelectorAll("[data-chat-action]").forEach(function (el) {
      el.addEventListener("click", function () {
        markConverted();
        if (firstTimer) clearTimeout(firstTimer);
        if (repeatTimer) clearTimeout(repeatTimer);
        stopHalo();
        clearBadge();
        dismissBubble();
        log("converted — nudges off");
      }, { once: true });
    });
  }

  var applied = false;
  function apply() {
    var widget = document.getElementById("custom-chat-widget");
    if (!widget) return false;
    var cta = widget.querySelector(".cta-buttons");
    if (!cta) return false;
    injectStyles();
    normaliseAuSalesClass(widget);
    reorderCtas(cta);
    applyDemoRename(cta.querySelector(".cta-demo"));
    var demo = cta.querySelector(".cta-demo");
    if (demo && !demo.__chatRenameObs) {
      var mo = new MutationObserver(function () { applyDemoRename(demo); });
      mo.observe(demo, { characterData: true, childList: true, subtree: true });
      demo.__chatRenameObs = mo;
    }
    if (!applied) {
      applied = true;
      scheduleAttentionNudge();
      log("apply complete");
    }
    return true;
  }

  function start() {
    if (apply()) return;
    var attempts = 0;
    var iv = setInterval(function () {
      attempts += 1;
      if (apply() || attempts > 50) clearInterval(iv);
    }, 100);
    log("polling for widget");
  }

  // Debug API
  window.chatWidgetEnhance = {
    testBeep:   function () { playChime({ force: true }); },
    testWiggle: function () { wiggleWidget(); },
    fire:       function () { fireAttentionBurst(); },
    reset:      function () {
      sessionStorage.removeItem(BEEP_KEY);
      sessionStorage.removeItem(BADGE_KEY);
      sessionStorage.removeItem(CONVERTED);
      sessionStorage.removeItem("chatWidgetBubbleDismissed-v4");
      log("session state cleared");
    },
  };

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start);
  else start();
})();



/* ──────────────────────────────────────────────────────────────────────────
 * Speediance chat widget — INLINE BOOKING (Marco)  2026-06-26
 *
 * Adds two things to the existing "Chat With Marco" widget, with NO markup
 * changes (pure JS enhancement, same pattern as the attention-nudge layer):
 *
 *   1. A live "Next available: today 3:30pm" teaser under the booking CTA,
 *      fed by https://crm.futuremvmt.com/cal/next-slot (visitor-tz aware,
 *      real Cal.com availability). Additive — hidden if the fetch fails.
 *
 *   2. An inline Cal.com booking panel that REPLACES the chat options when the
 *      CTA is tapped: a 15-min call / 30-min demo segmented toggle over Marco's
 *      real calendar (Cal.com inline embed). No new tab; back returns to chat.
 *
 * Region-agnostic (Marco is one Cal.com user). Append to assets/chat.js on the
 * UK + AU live themes. Debug: window.bmBooking.open() / .teaser().
 * ────────────────────────────────────────────────────────────────────────── */
(function bmInlineBooking() {
  "use strict";
  if (window.__bmInlineBooking) return;
  window.__bmInlineBooking = true;

  var CAL_ORIGIN = "https://cal.futuremvmt.com";
  var NEXT_SLOT_URL = "https://crm.futuremvmt.com/cal/next-slot";
  var BRAND = "#1166FF";

  var EVENTS = {
    quick_call: { key: "quick_call", calLink: "marco/quick-qa", label: "15-min call", sub: "Phone",      icon: icPhone() },
    full_demo:  { key: "full_demo",  calLink: "marco/full-demo", label: "30-min demo", sub: "Video call", icon: icVideo() },
  };
  var ORDER = ["quick_call", "full_demo"];

  function icPhone() { return '<svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.91.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z"/></svg>'; }
  function icVideo() { return '<svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2" ry="2"/></svg>'; }
  function icCal() { return '<svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>'; }
  function icBack() { return '<svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="15 18 9 12 15 6"/></svg>'; }
  function icClose() { return '<svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>'; }

  var widget = null, panel = null, selected = "quick_call", calLoaded = false, calReady = false, embedFailTimer = null;
  var miniNudgeEl = null, miniNudgePending = false;

  function visitorTz() {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/London"; }
    catch (e) { return "Europe/London"; }
  }

  function track(action) {
    var region = (widget && widget.dataset.region) || "";
    var payload = { event: "chat_widget_click", chat_widget_action: action, chat_widget_agent: (widget && widget.dataset.agentName) || "Marco", chat_widget_region: region };
    try { if (Array.isArray(window.dataLayer)) window.dataLayer.push(payload); } catch (e) {}
    try { if (typeof window.gtag === "function") window.gtag("event", "chat_widget_click", { action: action, agent: payload.chat_widget_agent, region: region }); } catch (e) {}
  }

  function injectStyles() {
    if (document.getElementById("bm-booking-css")) return;
    var s = document.createElement("style");
    s.id = "bm-booking-css";
    s.appendChild(document.createTextNode([
      ".cta-demo{display:block!important;text-align:left!important}",
      ".cta-demo .bm-demo-main{display:flex;align-items:center;gap:8px;font-weight:600;line-height:1.2}",
      ".cta-demo .bm-demo-main svg{flex:0 0 auto}",
      ".cta-demo .bm-demo-teaser{display:flex;align-items:center;gap:6px;margin-top:6px;padding-top:6px;border-top:1px solid rgba(255,255,255,.28);font-size:11.5px;font-weight:500;color:#e7f0ff}",
      ".cta-demo .bm-demo-teaser b{color:#fff;font-weight:600}",
      ".cta-demo .bm-dot{width:7px;height:7px;border-radius:50%;background:#77FF33;box-shadow:0 0 0 3px rgba(119,255,51,.3);flex:0 0 auto;animation:bmPulse 1.8s ease-out infinite}",
      "@keyframes bmPulse{0%{box-shadow:0 0 0 0 rgba(119,255,51,.45)}70%{box-shadow:0 0 0 7px rgba(119,255,51,0)}100%{box-shadow:0 0 0 0 rgba(119,255,51,0)}}",
      "#bm-booking-panel{position:fixed;bottom:30px;right:30px;z-index:10001;display:none;flex-direction:column;width:390px;max-width:calc(100vw - 24px);height:582px;max-height:calc(100vh - 56px);background:#fff;border-radius:18px;border-top:4px solid " + BRAND + ";box-shadow:0 18px 50px rgba(17,102,255,.22),0 6px 18px rgba(0,0,0,.12);overflow:hidden;font-family:'Inter',var(--font-body-family,sans-serif)}",
      "#bm-booking-panel.bm-on{display:flex;animation:bmIn .22s ease}",
      "@keyframes bmIn{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}}",
      ".bm-head{display:flex;align-items:center;gap:8px;padding:12px 12px 10px;border-bottom:1px solid #eef0f3}",
      ".bm-iconbtn{display:inline-flex;align-items:center;justify-content:center;width:30px;height:30px;border-radius:8px;border:0;background:transparent;color:#475569;cursor:pointer;padding:0}",
      ".bm-iconbtn:hover{background:#f1f5f9;color:#0f172a}",
      ".bm-head .bm-av{width:26px;height:26px;border-radius:50%;object-fit:cover}",
      ".bm-head .bm-title{font-size:13.5px;font-weight:600;color:#0f172a}",
      ".bm-head .bm-x{margin-left:auto}",
      ".bm-seg{display:flex;gap:4px;margin:12px 12px 0;background:#f1f5f9;padding:4px;border-radius:11px}",
      ".bm-seg button{flex:1;display:flex;flex-direction:column;align-items:center;gap:1px;padding:8px 4px;border:0;border-radius:8px;background:transparent;color:#64748b;cursor:pointer;transition:background .15s,color .15s}",
      ".bm-seg button .bm-sub{font-size:10px;opacity:.85}",
      ".bm-seg button .bm-lbl{font-size:11.5px;font-weight:600}",
      ".bm-seg button[aria-pressed='true']{background:" + BRAND + ";color:#fff;box-shadow:0 2px 8px rgba(17,102,255,.35)}",
      ".bm-seg button:focus-visible{outline:3px solid #0f172a;outline-offset:2px}",
      ".bm-cal-host{position:relative;flex:1;min-height:0;overflow:auto;margin:10px 6px 6px;-webkit-overflow-scrolling:touch}",
      ".bm-cal-host .bm-loading{position:absolute;top:0;left:0;right:0;height:340px;display:flex;align-items:center;justify-content:center;color:#94a3b8;font-size:12.5px;z-index:0}",
      ".bm-cal-host #bm-cal-embed{position:relative;z-index:1;min-height:430px}",
      ".bm-foot{display:flex;align-items:center;gap:6px;padding:8px 14px 12px;font-size:10.5px;color:#94a3b8}",
      ".bm-spin{display:flex;align-items:center;justify-content:center;height:100%;min-height:300px;color:#94a3b8;font-size:12.5px}",
      "@media (max-width:767px){#bm-booking-panel{left:8px;right:8px;bottom:8px;width:auto;max-width:none;height:86vh;height:86dvh;max-height:none}}",
      "#minimized-widget-icon.bm-mini{width:66px;height:66px;background:transparent;border:0;box-shadow:none;line-height:normal;font-size:0;overflow:visible;transition:transform .18s cubic-bezier(.23,1,.32,1)}",
      "#minimized-widget-icon.bm-mini .bm-mini-av{width:66px;height:66px;border-radius:50%;object-fit:cover;display:block;background:" + BRAND + ";border:3px solid #fff;box-shadow:0 8px 26px rgba(0,0,0,.30)}",
      "#minimized-widget-icon.bm-mini .bm-mini-wa{position:absolute;right:-3px;bottom:-3px;width:29px;height:29px;border-radius:50%;background:#25D366;border:3px solid #fff;box-shadow:0 2px 7px rgba(37,211,102,.45);display:flex;align-items:center;justify-content:center}",
      "#minimized-widget-icon.bm-mini .bm-mini-wa svg{width:17px;height:17px;display:block;fill:#fff}",
      "#minimized-widget-icon.bm-mini .bm-mini-pulse{position:absolute;inset:0;border-radius:50%;z-index:-1;animation:bmMiniPulse 2.2s ease-out infinite}",
      "@keyframes bmMiniPulse{0%{box-shadow:0 0 0 0 rgba(37,211,102,.5)}70%{box-shadow:0 0 0 17px rgba(37,211,102,0)}100%{box-shadow:0 0 0 0 rgba(37,211,102,0)}}",
      "#minimized-widget-icon.bm-mini:active{transform:scale(.95)}",
      "@media (hover:hover) and (pointer:fine){#minimized-widget-icon.bm-mini:hover{transform:scale(1.06)}}",
      ".bm-mini-nudge{position:fixed;right:24px;bottom:108px;z-index:10000;max-width:246px;background:#fff;color:#0f172a;border-radius:14px;box-shadow:0 12px 32px rgba(0,0,0,.22);padding:11px 13px 12px;font-family:'Inter',var(--font-body-family,sans-serif);cursor:pointer;opacity:0;transform:translateY(8px) scale(.96);transition:opacity .22s cubic-bezier(.23,1,.32,1),transform .22s cubic-bezier(.23,1,.32,1)}",
      ".bm-mini-nudge.show{opacity:1;transform:translateY(0) scale(1)}",
      ".bm-mini-nudge::after{content:'';position:absolute;right:22px;bottom:-5px;width:11px;height:11px;background:#fff;transform:rotate(45deg)}",
      ".bm-mini-nudge-name{font-size:11.5px;font-weight:600;color:#0f172a}",
      ".bm-mini-nudge-msg{font-size:11.5px;color:#475569;line-height:1.4;margin-top:2px;padding-right:10px}",
      ".bm-mini-nudge-cta{display:inline-flex;align-items:center;gap:4px;margin-top:8px;font-size:11.5px;font-weight:600;color:" + BRAND + "}",
      ".bm-mini-nudge-x{position:absolute;top:3px;right:5px;background:none;border:0;color:#94a3b8;font-size:15px;line-height:1;cursor:pointer;padding:2px 5px}",
      ".bm-mini-nudge-x:hover{color:#0f172a}",
      "@media (max-width:767px){.bm-mini-nudge{right:18px;bottom:102px;max-width:212px}}",
      "@media (prefers-reduced-motion: reduce){.cta-demo .bm-dot{animation:none}#bm-booking-panel.bm-on{animation:none}#minimized-widget-icon.bm-mini .bm-mini-pulse{animation:none}.bm-mini-nudge{transition:opacity .2s ease;transform:none}.bm-mini-nudge.show{transform:none}}"
    ].join("\n")));
    (document.head || document.documentElement).appendChild(s);
  }

  function buildPanel() {
    if (panel) return panel;
    var av = "";
    // Only accept a clean https URL — never interpolate an arbitrary src into innerHTML.
    try { var img = widget.querySelector(".agent-info img, #chat-toggle-btn img"); if (img && /^https:\/\/[^"'<>\s]+$/.test(img.src)) av = img.src; } catch (e) {}
    panel = document.createElement("div");
    panel.id = "bm-booking-panel";
    panel.setAttribute("role", "dialog");
    panel.setAttribute("aria-label", "Book a call or demo with Marco");
    panel.innerHTML =
      '<div class="bm-head">' +
        '<button type="button" class="bm-iconbtn bm-back" aria-label="Back to chat options">' + icBack() + '</button>' +
        (av ? '<img class="bm-av" src="' + av + '" alt="Marco" width="26" height="26"/>' : '') +
        '<span class="bm-title">Book with Marco</span>' +
        '<button type="button" class="bm-iconbtn bm-x" aria-label="Close">' + icClose() + '</button>' +
      '</div>' +
      '<div class="bm-seg" role="group" aria-label="Choose call or demo">' +
        ORDER.map(function (k) {
          var e = EVENTS[k];
          return '<button type="button" data-ev="' + k + '" aria-pressed="false">' +
            '<span style="display:flex;align-items:center;gap:6px">' + e.icon + '<span class="bm-lbl">' + e.label + '</span></span>' +
            '<span class="bm-sub">' + e.sub + '</span></button>';
        }).join("") +
      '</div>' +
      '<div class="bm-cal-host"><div class="bm-spin">Loading Marco’s calendar…</div></div>' +
      '<div class="bm-foot">' + icCal() + '<span style="margin-left:2px">Real-time availability · confirmed instantly</span></div>';
    document.body.appendChild(panel);

    panel.querySelector(".bm-back").addEventListener("click", backToOptions);
    panel.querySelector(".bm-x").addEventListener("click", minimizeAll);
    panel.querySelectorAll(".bm-seg button").forEach(function (b) {
      b.addEventListener("click", function () { selectEvent(b.getAttribute("data-ev")); });
    });
    return panel;
  }

  function loadCal() {
    if (calLoaded) return;
    calLoaded = true;
    (function (C, A, L) {
      var p = function (a, ar) { a.q.push(ar); };
      var d = C.document;
      C.Cal = C.Cal || function () {
        var cal = C.Cal, ar = arguments;
        if (!cal.loaded) { cal.ns = {}; cal.q = cal.q || []; d.head.appendChild(d.createElement("script")).src = A; cal.loaded = true; }
        if (ar[0] === L) {
          var api = function () { p(api, arguments); };
          var ns = ar[1];
          api.q = api.q || [];
          if (typeof ns === "string") { cal.ns[ns] = cal.ns[ns] || api; p(cal.ns[ns], ar); p(cal, ["initNamespace", ns]); }
          else p(cal, ar);
          return;
        }
        p(cal, ar);
      };
    })(window, CAL_ORIGIN + "/embed/embed.js", "init");
    window.Cal("init", { origin: CAL_ORIGIN });
    window.Cal("ui", { theme: "light", styles: { branding: { brandColor: BRAND } }, cssVarsPerTheme: { light: { "cal-brand": BRAND } }, hideEventTypeDetails: false, layout: "month_view" });
    try { window.Cal("on", { action: "bookingSuccessful", callback: function () { track("booking_success"); } }); } catch (e) {}
    calReady = true;
  }

  function renderEmbed(calLink) {
    var host = panel.querySelector(".bm-cal-host");
    host.innerHTML = '<div class="bm-loading">Loading Marco’s calendar…</div><div id="bm-cal-embed"></div>';
    if (embedFailTimer) { clearTimeout(embedFailTimer); embedFailTimer = null; }
    function fallback() {
      var h = panel && panel.querySelector(".bm-cal-host");
      if (h) h.innerHTML = '<div class="bm-spin">Couldn’t load the calendar here. <a href="' + CAL_ORIGIN + "/" + calLink + '" target="_blank" rel="noopener" style="color:' + BRAND + ';margin-left:6px;font-weight:600">Open booking page ↗</a></div>';
    }
    try {
      window.Cal("inline", { elementOrSelector: "#bm-cal-embed", calLink: calLink, config: { layout: "month_view", theme: "light" } });
    } catch (e) { fallback(); return; }
    // If embed.js is blocked (CSP / ad-blocker / offline) no iframe ever mounts —
    // swap the spinner for a direct booking link instead of spinning forever.
    embedFailTimer = setTimeout(function () {
      var el = panel && panel.querySelector("#bm-cal-embed");
      if (el && !el.querySelector("iframe")) fallback();
    }, 6000);
  }

  function selectEvent(key) {
    if (!EVENTS[key]) key = "quick_call";
    // ignore a re-tap of the already-active event once its calendar has mounted
    if (key === selected && panel.querySelector("#bm-cal-embed iframe")) return;
    selected = key;
    panel.querySelectorAll(".bm-seg button").forEach(function (b) {
      b.setAttribute("aria-pressed", b.getAttribute("data-ev") === key ? "true" : "false");
    });
    loadCal();
    renderEmbed(EVENTS[key].calLink);
    track("book_" + key);
  }

  function openBooking(preferKey) {
    injectStyles();
    buildPanel();
    if (widget) widget.style.display = "none";
    var mini = document.getElementById("minimized-widget-icon");
    if (mini) mini.style.display = "none";
    panel.classList.add("bm-on");
    selectEvent(EVENTS[preferKey] ? preferKey : selected);
    track("book_open");
  }

  function backToOptions() {
    if (panel) panel.classList.remove("bm-on");
    if (widget) { widget.style.display = "block"; var cb = document.getElementById("chat-box"); if (cb && !isMobile()) cb.style.display = "block"; }
  }

  function minimizeAll() {
    if (panel) panel.classList.remove("bm-on");
    var region = (widget && widget.dataset.region) === "AU" ? "au" : "uk";
    try { sessionStorage.setItem(region + "ChatWidgetMinimized", "1"); } catch (e) {}
    if (widget) widget.style.display = "none";
    var mini = document.getElementById("minimized-widget-icon");
    if (mini) mini.style.display = "block";
  }

  function isMobile() { try { return window.matchMedia("(max-width: 767px)").matches; } catch (e) { return false; } }

  function setupTeaser(demo) {
    var main = demo.querySelector(".bm-demo-main");
    if (!main) {
      main = document.createElement("span");
      main.className = "bm-demo-main";
      main.innerHTML = icCal() + "<span>Book a quick call or demo</span>";
      demo.textContent = "";
      demo.appendChild(main);
    }
    var teaser = demo.querySelector(".bm-demo-teaser");
    if (!teaser) {
      teaser = document.createElement("span");
      teaser.className = "bm-demo-teaser";
      teaser.style.display = "none";
      demo.appendChild(teaser);
    }
    return teaser;
  }

  function fetchTeaser(teaser) {
    var ctrl = ("AbortController" in window) ? new AbortController() : null;
    var settled = false;
    var to = setTimeout(function () { if (!settled) { settled = true; if (ctrl) { try { ctrl.abort(); } catch (e) {} } } }, 2500);
    // Race the fetch against the timeout so it can never stall even where
    // AbortController is unavailable (old Safari / some WebViews).
    var req = fetch(NEXT_SLOT_URL + "?event=soonest&tz=" + encodeURIComponent(visitorTz()), ctrl ? { signal: ctrl.signal } : undefined)
      .then(function (r) { return r.ok ? r.json() : null; })
      .catch(function () { return null; });
    var timeout = new Promise(function (resolve) { setTimeout(function () { resolve(null); }, 2500); });
    Promise.race([req, timeout]).then(function (j) {
      settled = true; clearTimeout(to);
      if (!j || !j.label) return; // additive: stay hidden on failure/empty
      if (j.event && EVENTS[j.event]) selected = j.event;
      teaser.innerHTML = '<span class="bm-dot"></span><span>Next available: <b>' + escapeHtml(j.label) + "</b></span>";
      teaser.style.display = "flex";
    });
  }

  function escapeHtml(s) { return String(s).replace(/[&<>"]/g, function (c) { return { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]; }); }

  function wireCta() {
    var demo = widget.querySelector(".cta-demo");
    if (!demo || demo.__bmWired) return !!demo;
    demo.__bmWired = true;
    var teaser = setupTeaser(demo);
    demo.addEventListener("click", function (e) { e.preventDefault(); openBooking(selected); });
    fetchTeaser(teaser);
    return true;
  }

  // ── Minimized launcher: agent face + live "online" dot + soft pulse, and a
  //    once-per-session re-engagement nudge that re-opens straight to booking.
  function miniAvatar() {
    try {
      var img = widget && widget.querySelector(".agent-info img, #chat-toggle-btn img");
      if (img && /^https:\/\/[^"'<>\s]+$/.test(img.src)) return img.src;
    } catch (e) {}
    return "";
  }
  function waSvg() { return '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99-.001-3.951-.5-5.688-1.448l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884-.001 2.225.651 3.891 1.746 5.634l-.999 3.648 3.742-.981zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/></svg>'; }
  function enhanceMiniIcon() {
    var mini = document.getElementById("minimized-widget-icon");
    if (!mini) return;
    if (!mini.__bmMini) {
      mini.__bmMini = true;
      mini.classList.add("bm-mini");
      mini.setAttribute("aria-label", "Message Marco on WhatsApp");
      mini.innerHTML = '<span class="bm-mini-pulse" aria-hidden="true"></span><img class="bm-mini-av" alt=""/><span class="bm-mini-wa" aria-hidden="true">' + waSvg() + '</span>';
    }
    var av = miniAvatar(), im = mini.querySelector(".bm-mini-av");
    if (im && av) im.src = av;
  }
  function observeMini() {
    var mini = document.getElementById("minimized-widget-icon");
    if (!mini || mini.__bmMiniObs) return;
    mini.__bmMiniObs = true;
    var obs = new MutationObserver(function () {
      if (mini.style.display !== "none") maybeShowMiniNudge();  // fixed-pos: offsetParent is always null, use display
      else hideMiniNudge();
    });
    obs.observe(mini, { attributes: true, attributeFilter: ["style"] });
    if (mini.style.display === "block") maybeShowMiniNudge();
  }
  function maybeShowMiniNudge() {
    if (miniNudgeEl || miniNudgePending) return;
    try { if (sessionStorage.getItem("bmMiniNudgeSeen") === "1") return; } catch (e) {}
    miniNudgePending = true;
    setTimeout(function () {
      miniNudgePending = false;
      var mini = document.getElementById("minimized-widget-icon");
      if (!mini || mini.style.display === "none") return;
      try { sessionStorage.setItem("bmMiniNudgeSeen", "1"); } catch (e) {}
      buildMiniNudge();
    }, 1800);
  }
  function buildMiniNudge() {
    if (miniNudgeEl) return;
    miniNudgeEl = document.createElement("div");
    miniNudgeEl.className = "bm-mini-nudge";
    miniNudgeEl.setAttribute("role", "button");
    miniNudgeEl.setAttribute("tabindex", "0");
    miniNudgeEl.innerHTML =
      '<button class="bm-mini-nudge-x" type="button" aria-label="Dismiss">×</button>' +
      '<div class="bm-mini-nudge-name">Hi, I’m Marco 👋</div>' +
      '<div class="bm-mini-nudge-msg">Still here — book a quick call or demo</div>' +
      '<div class="bm-mini-nudge-cta">Book a time →</div>';
    document.body.appendChild(miniNudgeEl);
    miniNudgeEl.querySelector(".bm-mini-nudge-x").addEventListener("click", function (e) { e.stopPropagation(); dismissMiniNudge(); });
    miniNudgeEl.addEventListener("click", function () { dismissMiniNudge(); openBooking(selected); });
    requestAnimationFrame(function () { setTimeout(function () { if (miniNudgeEl) miniNudgeEl.classList.add("show"); }, 20); });
  }
  function hideMiniNudge() {
    if (!miniNudgeEl) return;
    var el = miniNudgeEl; miniNudgeEl = null;
    el.classList.remove("show");
    setTimeout(function () { if (el.parentNode) el.parentNode.removeChild(el); }, 240);
  }
  function dismissMiniNudge() { try { sessionStorage.setItem("bmMiniNudgeSeen", "1"); } catch (e) {} hideMiniNudge(); }

  function setupMini() { enhanceMiniIcon(); observeMini(); }

  function start() {
    widget = document.getElementById("custom-chat-widget");
    if (widget && wireCta()) { injectStyles(); setupMini(); return; }
    var n = 0;
    var iv = setInterval(function () {
      widget = document.getElementById("custom-chat-widget");
      n += 1;
      if ((widget && wireCta()) || n > 50) { injectStyles(); setupMini(); clearInterval(iv); }
    }, 100);
  }

  window.bmBooking = { open: function (k) { openBooking(k); }, back: backToOptions, teaser: function () { var d = widget && widget.querySelector(".cta-demo"); if (d) fetchTeaser(setupTeaser(d)); } };

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", start);
  else start();
})();
